import React, { Fragment, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import dayjs from 'dayjs';
import PaymentIcon from '@mui/icons-material/Payment';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import DeleteIcon from '@mui/icons-material/Delete';
import ActualizarIcon from '@mui/icons-material/Update';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import PropTypes from 'prop-types';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridFooterContainer,
  GridPagination,
  GridToolbarContainer,
  GridToolbarExport
} from '@mui/x-data-grid';

function CustomToolbar({ rows }) {
  const handleExportExcel = () => {
    // Prepare data for Excel export
    const excelData = rows.map((row) => ({
      Cliente: row.customer_name,
      Item: row.item_name,
      Horas: row.horas,
      From: row.fromHour || '',
      To: row.toHour || '',
      Costo: row.costo,
      Total: row.horas * row.costo,
      Día: new Date(row.dia).toLocaleDateString('es-ES'),
      Observaciones: row.observaciones || '',
      Pagado: row.pagado ? 'Sí' : 'No'
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const columnWidths = [
      { wch: 20 }, // Cliente
      { wch: 20 }, // Item
      { wch: 8 }, // Horas
      { wch: 10 }, // Costo
      { wch: 10 }, // Total
      { wch: 12 }, // Día
      { wch: 25 }, // Observaciones
      { wch: 8 } // Pagado
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte Horas');

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `reporte_horas_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <GridToolbarContainer>
      <GridToolbarExport />
      <Button color="primary" startIcon={<FileDownloadIcon />} onClick={handleExportExcel} size="small">
        Exportar a Excel
      </Button>
    </GridToolbarContainer>
  );
}

export function TotalComponent(props) {
  return (
    <Typography variant="body2" sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}>
      Total: {props.total.toLocaleString('es-ES', { maximumFractionDigits: 2 })} &nbsp;·&nbsp; Total horas:{' '}
      {props.totalHoras}
    </Typography>
  );
}

function CustomFooter(props) {
  return (
    <GridFooterContainer>
      <TotalComponent total={props.total} totalHoras={props.totalHoras} />
      <GridPagination />
    </GridFooterContainer>
  );
}

CustomFooter.propTypes = {
  total: PropTypes.number
};

const ReporteHoras = (props) => {
  const location = useLocation();
  const initialCustomerIds = Array.isArray(location.state?.customerIds) ? [...location.state.customerIds] : [];
  const initialItemIds = Array.isArray(location.state?.itemIds) ? [...location.state.itemIds] : [];
  const initialFilterPagado = typeof location.state?.pagado === 'boolean' ? location.state.pagado : null;
  const initialFechaDesde = location.state?.fechaDesde ? dayjs(location.state.fechaDesde) : null;
  const initialFechaHasta = location.state?.fechaHasta ? dayjs(location.state.fechaHasta) : null;
  const [snackbar, setSnackbar] = useState(null);
  const [rowModesModel, setRowModesModel] = useState({});
  const [rows, setRows] = useState(props.items);
  const [dataCustomers, setDataCustomers] = useState([]);
  const [dataItems, setDataItems] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedItems, setSelectedItems] = useState(initialItemIds);
  const [selectedCustomers, setSelectedCustomers] = useState(initialCustomerIds);
  const [filterPagado, setFilterPagado] = useState(initialFilterPagado);
  const [pagadoId, setPagadoId] = useState(null);
  const [marcarComoPagado, setMarcarComoPagado] = useState(true);
  const [marcarMesPago, setMarcarMesPago] = useState(true);
  const [borradoId, setBorradoId] = useState(null);
  const [actualizarId, setActualizarId] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalHoras, setTotalHoras] = useState(0);
  const [fechaDesde, setFechaDesde] = useState(initialFechaDesde);
  const [fechaHasta, setFechaHasta] = useState(initialFechaHasta);

  const handleClose = () => {
    setPagadoId(null);
    setMarcarComoPagado(true);
    setMarcarMesPago(true);
    setBorradoId(null);
    setActualizarId(null);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: response } = await axios.request({
          method: 'get',
          url: 'customers'
        });
        response.push({ name: 'כולם', id: -1 });
        setDataCustomers(response);
        const { data: items } = await axios.request({
          method: 'get',
          url: 'items'
        });
        items.push({ name: 'כולם', id: -1 });
        setDataItems(items);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const handleCloseSnackbar = () => setSnackbar(null);

  const columns = [
    { field: 'id', headerName: 'ID', width: 40, hide: true },
    {
      field: 'customer_name',
      headerName: 'Cliente ',
      width: 120,
      editable: false
    },
    {
      field: 'item_name',
      headerName: 'Item ',
      width: 120,
      editable: false
    },
    {
      field: 'horas',
      headerName: 'Horas',
      width: 70,
      type: 'number',
      editable: false
    },
    {
      field: 'costo',
      headerName: 'Costo',
      width: 80,
      type: 'number',
      editable: false
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 80,
      type: 'number',
      valueGetter: (params) => params.row.horas * params.row.costo,
      editable: false
    },
    {
      field: 'dia',
      headerName: 'Dia',
      width: 120,
      editable: false,
      type: 'date',
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('he-HE');
      }
    },
    {
      field: 'observaciones',
      headerName: 'Observaciones',
      width: 180,
      editable: true,
      type: 'string'
    },
    {
      field: 'fromHour',
      headerName: 'Desde',
      width: 90,
      editable: false
    },
    {
      field: 'toHour',
      headerName: 'Hasta',
      width: 90,
      editable: false
    },
    {
      field: 'pagado',
      headerName: 'Pagado',
      width: 80,
      editable: true,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 140,
      cellClassName: 'actions',
      getActions: (data) => {
        return [
          data.row.pagado ? (
            <GridActionsCellItem
              icon={<MoneyOffIcon />}
              label="Marcar no pagado"
              title="Marcar no pagado"
              className="textPrimary"
              onClick={marcarNoPagadoDialog(data.id)}
              color="inherit"
            />
          ) : (
            <GridActionsCellItem
              icon={<PaymentIcon />}
              label="Marcar pagado"
              title="Marcar pagado"
              className="textPrimary"
              onClick={marcarPagadoDialog(data.id)}
              color="inherit"
            />
          ),
          <GridActionsCellItem
            icon={<DeleteIcon />}
            disabled={data.row.pagado}
            label="Delete"
            title="Borrar"
            onClick={handleDeleteClick(data.id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<ActualizarIcon />}
            disabled={parseInt(data.row.costo) === parseInt(data.row.item_costo)}
            label="Actualizar costo item"
            title="Actualizar costo item"
            className="textPrimary"
            onClick={actualizarCostoDialog(data.id)}
            color="inherit"
          />
        ];
      }
    }
  ];

  const marcarPagado = () => {
    const pagar = async () => {
      await axios.put(`horas/${pagadoId}/pagado`, {
        marcarMesPago,
        pagado: marcarComoPagado
      });
      setDataLoaded(false);
      setPagadoId(null);
      setMarcarComoPagado(true);
      setMarcarMesPago(true);
    };
    pagar();
  };

  const actualizarCostoItem = () => {
    const actualizarItem = async () => {
      await axios.put(`horas/${actualizarId}/actualizar-costo`);
      setDataLoaded(false);
      setActualizarId(null);
    };
    actualizarItem();
  };

  const marcarPagadoDialog = (id) => () => {
    setPagadoId(id);
    setMarcarComoPagado(true);
  };

  const marcarNoPagadoDialog = (id) => () => {
    setPagadoId(id);
    setMarcarComoPagado(false);
  };

  const actualizarCostoDialog = (id) => () => {
    setActualizarId(id);
  };

  const handleNewCustomer = async (customers) => {
    setSelectedCustomers((customers || []).map((item) => item.id));
  };

  const handleNewItem = async (items) => {
    setSelectedItems((items || []).map((item) => item.id));
  };

  const handleDeleteClick = (id) => () => {
    setBorradoId(id);
  };

  const borrar = () => {
    const borrarFunc = async () => {
      await axios.delete(`horas/${borradoId}`);
      setDataLoaded(false);
      setBorradoId(null);
    };
    borrarFunc();
  };

  const handleDesdeChange = (val) => {
    setDataLoaded(false);
    setFechaDesde(val);
  };

  const handleHastaChange = (val) => {
    setDataLoaded(false);
    setFechaHasta(val);
  };

  const processRowUpdate = async (newRow) => {
    try {
      await axios.put(`horas/${newRow.id}/observaciones`, {
        observaciones: newRow.observaciones
      });
      setSnackbar({ children: 'Observaciones actualizadas correctamente', severity: 'success' });
      return newRow;
    } catch (error) {
      setSnackbar({ children: 'Error al actualizar observaciones', severity: 'error' });
      throw error;
    }
  };

  const handleProcessRowUpdateError = (error) => {
    setSnackbar({ children: 'Error al guardar cambios', severity: 'error' });
  };

  useEffect(() => {
    (async () => {
      const hasBaseFilters = selectedCustomers.length > 0 || selectedItems.length > 0 || fechaDesde || fechaHasta;
      if (!hasBaseFilters) {
        return;
      }

      const params = new URLSearchParams();
      let hasAllCustomers = false;
      let hasAllItems = false;
      if (selectedCustomers) {
        selectedCustomers.forEach((customerId) => {
          params.append('customerIds', customerId);
          hasAllCustomers = hasAllCustomers || customerId === -1;
        });
      }

      if (hasAllCustomers) {
        params.delete('customerIds');
      }

      if (selectedItems) {
        selectedItems.forEach((itemId) => {
          params.append('itemIds', itemId);
          hasAllItems = hasAllItems || itemId === -1;
        });
      }

      if (hasAllItems) {
        params.delete('itemIds');
      }

      if (fechaDesde) {
        params.append('fechaDesde', fechaDesde.format('YYYY-MM-DD'));
      }

      if (fechaHasta) {
        params.append('fechaHasta', fechaHasta.format('YYYY-MM-DD'));
      }

      const response = await axios.request({
        method: 'get',
        url: `horas`,
        params: params
      });

      const responseRows = Array.isArray(response.data) ? response.data : [];
      const filteredRows =
        filterPagado === null ? responseRows : responseRows.filter((row) => Boolean(row.pagado) === filterPagado);

      setRows(filteredRows);
      setDataLoaded(true);
    })();
  }, [selectedCustomers, selectedItems, dataLoaded, fechaDesde, fechaHasta, filterPagado]);

  const hasActiveFilters = selectedCustomers.length > 0 || selectedItems.length > 0 || !!fechaDesde || !!fechaHasta;

  return (
    <Fragment>

      {/* Filter bar */}
      <Box
        component="form"
        sx={{
          mb: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.25,
          alignItems: 'center',
          background: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: 1.5
        }}
        noValidate
        autoComplete="off">
        <Autocomplete
          sx={{ minWidth: { xs: '100%', md: 180 }, flex: 1 }}
          multiple
          disablePortal
          id="customerId"
          value={dataCustomers.filter((customer) => selectedCustomers.includes(customer.id))}
          getOptionLabel={(d) => d.name}
          filterSelectedOptions
          options={dataCustomers}
          onChange={(e, values) => {
            setDataLoaded(false);
            handleNewCustomer(values);
          }}
          renderInput={(params) => <TextField {...params} label="Cliente" variant="outlined" placeholder="Clientes" />}
        />
        <Autocomplete
          sx={{ minWidth: { xs: '100%', md: 180 }, flex: 1 }}
          multiple
          disablePortal
          id="itemId"
          value={dataItems.filter((item) => selectedItems.includes(item.id))}
          filterSelectedOptions
          getOptionLabel={(d) => d.name}
          options={dataItems}
          onChange={(e, values) => {
            setDataLoaded(false);
            handleNewItem(values);
          }}
          renderInput={(params) => <TextField {...params} label="Item" variant="outlined" placeholder="Items" />}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label="Fecha desde"
            format="DD/MM/YYYY"
            value={fechaDesde}
            onChange={handleDesdeChange}
            slotProps={{
              textField: { variant: 'outlined' }
            }}
          />
          <DesktopDatePicker
            label="Fecha hasta"
            format="DD/MM/YYYY"
            value={fechaHasta}
            onChange={handleHastaChange}
            slotProps={{
              textField: { variant: 'outlined' }
            }}
          />
        </LocalizationProvider>
        {filterPagado !== null && (
          <Chip
            label={filterPagado ? 'Solo pagados' : 'Solo pendientes'}
            onDelete={() => {
              setDataLoaded(false);
              setFilterPagado(null);
            }}
            sx={{ alignSelf: 'center' }}
          />
        )}
      </Box>
      <Box
        sx={{
          height: 'calc(100vh - 184px)',
          minHeight: 460,
          padding: 0,
          width: '100%',
          '& .actions': {
            color: 'text.secondary'
          },
          '& .textPrimary': {
            color: 'text.primary'
          }
        }}>
        {!hasActiveFilters && !dataLoaded && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ height: 260, gap: 2 }}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: 'action.hover',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <FilterListIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                Selecciona filtros para ver los registros
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Usa los filtros de arriba para buscar horas registradas
              </Typography>
            </Box>
          </Box>
        )}
        {hasActiveFilters && !dataLoaded && (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 260 }}>
            <CircularProgress sx={{ color: '#0d9488' }} />
          </Box>
        )}
        {dataLoaded && (
          <DataGrid
            rows={rows}
            slots={{
              toolbar: CustomToolbar,
              footer: (props) => <CustomFooter {...props} total={total} totalHoras={totalHoras} />
            }}
            slotProps={{
              toolbar: { rows }
            }}
            columns={columns}
            rowModesModel={rowModesModel}
            onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                minHeight: '36px !important',
                maxHeight: '36px !important'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'nowrap'
              },
              '& .MuiDataGrid-cell': {
                minHeight: '36px !important',
                maxHeight: '36px !important'
              }
            }}
            onStateChange={(state) => {
              const visibleRows = state.visibleRowsLookup;
              let visibleItems = [];
              for (const [id, value] of Object.entries(visibleRows)) {
                if (value === true) {
                  visibleItems.push(parseInt(id));
                }
              }
              const res = rows.filter((item) => visibleItems.includes(item.id));
              const total = res
                .map((item) => parseFloat(item.costo) * parseFloat(item.horas))
                .reduce((a, b) => a + b, 0);
              const totalHoras = res.map((item) => parseFloat(item.horas)).reduce((a, b) => a + b, 0);
              setTotal(total);
              setTotalHoras(totalHoras);
            }}
          />
        )}
        {!!snackbar && (
          <Snackbar
            open
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            onClose={handleCloseSnackbar}
            autoHideDuration={6000}>
            <Alert {...snackbar} onClose={handleCloseSnackbar} />
          </Snackbar>
        )}
      </Box>
      <Dialog open={pagadoId !== null} onClose={handleClose} aria-labelledby="responsive-dialog-title">
        <DialogTitle id="responsive-dialog-title">
          {marcarComoPagado ? 'Marcar pagado?' : 'Marcar no pagado?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {marcarComoPagado
              ? '¿Estás seguro de que quieres marcar estas horas como pagadas?'
              : '¿Estás seguro de que quieres revertir estas horas y marcarlas como no pagadas?'}
          </DialogContentText>
          <FormControlLabel
            control={<Checkbox checked={marcarMesPago} onChange={(event) => setMarcarMesPago(event.target.checked)} />}
            label={marcarComoPagado ? 'Marcar todas las horas del mes pagas' : 'Marcar todas las horas del mes no pagas'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            No
          </Button>
          <Button onClick={marcarPagado} autoFocus color={marcarComoPagado ? 'primary' : 'error'}>
            {marcarComoPagado ? 'Sí, marcar pagado' : 'Sí, marcar no pagado'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={actualizarId !== null} onClose={handleClose} aria-labelledby="responsive-dialog-title">
        <DialogTitle id="responsive-dialog-title">{'Actualizar costo item?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>Actualizar costo?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            No
          </Button>
          <Button onClick={actualizarCostoItem} autoFocus>
            Si
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={borradoId !== null} onClose={handleClose} aria-labelledby="responsive-dialog-title">
        <DialogTitle id="responsive-dialog-title">{'Borrar?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>Confirma borrado?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            No
          </Button>
          <Button onClick={borrar} autoFocus>
            Si
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default ReporteHoras;
